import { Observable, throwError } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';

import {
  HttpEvent,
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { SN_API_URL } from '../providers/api-url.provider';

@Injectable()
export class ApiErrorInterceptor implements HttpInterceptor {
  constructor(
    @Inject(SN_API_URL) private readonly _apiUrl: string,
    private readonly _snackbar: MatSnackBar,
  ) {}

  public intercept(request: HttpRequest<any>, handler: HttpHandler): Observable<HttpEvent<any>> {
    if (!request.url.startsWith(this._apiUrl)) {
      return handler.handle(request);
    }

    if (request.headers.get('X-fs-skip-error')) {
      return handler.handle(request.clone({ headers: request.headers.delete('X-fs-skip-error') }));
    }

    return handler.handle(request).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        switch (errorResponse.status) {
          case 0:
            return this._handle0Error(errorResponse);
          case 500:
            return this._handle500Error(errorResponse);
        }

        return throwError(errorResponse);
      }),
    );
  }

  /**
   * Handles errors with status code 0.
   * A status code of 0 generally means there was no response from the server.
   * This can occur for various reasons such as network issues, server timeout, or being offline.
   */
  private _handle0Error(errorResponse: HttpErrorResponse): Observable<HttpEvent<any>> {
    const snackbarRef = this._snackbar.open(
      'No response from server. Please check your internet connection or try again later.',
      'Retry',
    );

    snackbarRef
      .afterDismissed()
      .pipe(filter((action) => !!action.dismissedByAction))
      .subscribe(() => {
        location.reload();
      });

    return throwError(errorResponse);
  }

  private _handle500Error(errorResponse: HttpErrorResponse): Observable<HttpEvent<any>> {
    const snackbarRef = this._snackbar.open(
      "Something went wrong. Sorry for the inconvenience, we're working on it.",
      'Reload',
    );

    snackbarRef
      .afterDismissed()
      .pipe(filter((action) => !!action.dismissedByAction))
      .subscribe(() => {
        location.reload();
      });

    return throwError(errorResponse);
  }
}
